import { BulkEditTarget } from './BulkEditServiceDialog.props';

/**
 * Pairs each selected job id with whether it is closed, which the id list on
 * its own can't carry.
 *
 * A row is closed when its status isn't one of the open ones. While the
 * statuses query is still resolving `openStatusNames` is empty, and claiming
 * every job open would quietly bypass the closed-job permission check — so
 * closed state is left undefined until the names are known.
 */
/**
 * The distinct service type names among the selected jobs.
 *
 * Read off the rendered rows, so it costs no request. Jobs selected on another
 * page are absent — the caller warns on what is known rather than claiming the
 * selection is uniform.
 */
export const selectedServiceTypeNames = (
  selectedIds: readonly number[],
  rows: readonly Record<string, unknown>[]
): string[] => {
  const ids = new Set(selectedIds);
  const names = new Set<string>();
  for (const row of rows) {
    const id = row.serviceId as number | undefined;
    const name = row.serviceTypeName as string | undefined;
    if (typeof id === 'number' && ids.has(id) && typeof name === 'string' && name !== '') {
      names.add(name);
    }
  }
  return [...names];
};

export const buildBulkEditTargets = (
  selectedIds: readonly number[],
  rows: readonly Record<string, unknown>[],
  openStatusNames: readonly string[]
): BulkEditTarget[] => {
  const openNames = new Set(openStatusNames);
  if (openNames.size === 0) return selectedIds.map((id) => ({ id }));

  const statusById = new Map<number, string>();
  for (const row of rows) {
    const id = row.serviceId as number | undefined;
    const name = row.serviceStatusName as string | undefined;
    if (typeof id === 'number' && typeof name === 'string') statusById.set(id, name);
  }

  return selectedIds.map((id) => {
    const statusName = statusById.get(id);
    return statusName === undefined ? { id } : { id, isClosed: !openNames.has(statusName) };
  });
};
